import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { setUserTimeZone } from '../../utils/dateTime';
import {
  FolderOpen, X, AlertTriangle, RefreshCw, Settings, Pencil, Save, UserCheck,
} from 'lucide-react';
import { databasesApi } from '../../services/api';
import { activeDbSlug } from '../../services/activeDatabase';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  usePreferences, getMachineId, applyThemeToDom, applyZoomToDom, applyDensityToDom, applyMotionToDom,
} from '../../context/PreferencesContext';
import {
  setDownloadLocation,
  getLocationLabel,
  clearDownloadLocation,
  isFileSystemAccessSupported,
} from '../../utils/downloadLocation';
import LocalDevTab from './LocalDevTab';
import ThemesTab from './ThemesTab';
import ThemePickerRow from './themes/ThemePickerRow';
import StuiSelect from '../../components/common/StuiSelect';

// ── Constants ────────────────────────────────────────────────────────────────
// 'Themes' is the SysAdmin workshop (list, compare, builder); everyone picks a theme on Display.
// A theme pick is a trial (PreferencesContext) aimed at the theme's Apply to scope, so it
// survives browsing away; header Save or the footer Apply keeps it.
// 'Local Dev' is not catalog-driven — it renders LocalDevTab (file-backed status
// panel for appsettings.Development.json) and only appears on dev VMs.
const TAB_ORDER = ['General', 'Display', 'Themes', 'Downloads', 'Notifications', 'Security', 'Debug', 'Local Dev'];

const LOCATION_KEYS = [
  { key: 'app-default',    label: 'Application Default',  sub: 'Fallback for all downloads when no specific location is set'  },
  { key: 'local-reports',  label: 'Local Report Exports', sub: 'PDF, Excel, and CSV reports generated on this machine'        },
  { key: 'remote-reports', label: 'Remote Re-downloads',  sub: "Reports re-downloaded from another machine's history"         },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
const detectSession = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown', os = 'Unknown', device = 'PC';
  if      (ua.includes('Edg/'))                            browser = 'Microsoft Edge';
  else if (ua.includes('Chrome/') && !ua.includes('OPR'))  browser = 'Chrome';
  else if (ua.includes('Firefox/'))                        browser = 'Firefox';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('OPR/') || ua.includes('Opera/'))   browser = 'Opera';
  if      (ua.includes('Windows NT'))  os = 'Windows';
  else if (ua.includes('Mac OS X'))    os = 'macOS';
  else if (ua.includes('Android'))     os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Linux'))       os = 'Linux';
  if      (ua.includes('Mobile'))  device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad'))   device = 'Tablet';
  return { browser, os, device };
};

const parseOptions = (def) => {
  if (!def?.optionsJson) return null;
  try { return JSON.parse(def.optionsJson); } catch { return null; }
};

const scopeKeyOf = (row) => `${row.prefKey}|${row.databaseInstanceId ?? ''}|${row.machineId ?? ''}`;

// ── Sub-components ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, underConstruction = false }) => (
  <div className="stui-detail-section-header">
    <span>{title}</span>
    {underConstruction && <span className="stui-badge-construction">Under Construction</span>}
  </div>
);

// Badge renders to the RIGHT of the preference label (Josh 2026-08-12)
const ToggleRow = ({ label, sub, checked, onChange, disabled = false, badge = false }) => (
  <div className="stui-row-setting">
    <div className="stui-row-setting-label">
      <div className="stui-label">{label}{badge && <span className="stui-badge-construction">Under Construction</span>}</div>
      {sub && <div className="stui-sublabel">{sub}</div>}
    </div>
    <label className="stui-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span className="stui-toggle-slider" />
    </label>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
const PreferencesPage = () => {
  const { currentUser, isImpersonating, isSysAdmin } = useAuth();
  const { notify } = useNotifications();
  const {
    hydrated, failSoft, catalog, rows, slugToDbId,
    getPref, savePrefs, restoreDefaults, reapplyVisuals,
    themes, trial, startTrial, setTrialScope, stopTrial,
  } = usePreferences();

  // A theme picked on this page and not yet kept. The trial outlives the page, so coming back
  // reopens editing on the Display tab with that pick and its scope.
  const themeTrial = trial?.source === 'preferences' ? trial : null;

  const [activeTab, setActiveTab] = useState(themeTrial ? 'Display' : 'General');
  const [isEditing, setIsEditing] = useState(!!themeTrial);
  const [dirty, setDirty] = useState(new Map());          // scopeKey → write row
  const [saveErrors, setSaveErrors] = useState({});       // prefKey → message
  const [saving, setSaving] = useState(false);
  // Scope target per pref: 'global' | 'machine' | <numeric dbId>.
  // DB-scoped prefs use a combo (All Databases + each accessible DB) so any
  // database's override is editable from anywhere — pills would spill as DBs grow.
  const [scopeTarget, setScopeTarget] = useState(() => (themeTrial ? { 'display.theme': themeTrial.scope } : {}));
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [locationLabels, setLocationLabels] = useState({ 'app-default': null, 'local-reports': null, 'remote-reports': null });
  const [restoreConfirm, setRestoreConfirm] = useState(false);

  const fsSupported = isFileSystemAccessSupported();
  const session = detectSession();
  const machineId = getMachineId();

  // Local Dev tab stages its own file-backed edits; it reports dirtiness here
  // and exposes its save routine via ref so the page header Save applies both.
  const [localDevDirty, setLocalDevDirty] = useState(false);
  const localDevSaveRef = useRef(null);
  const isDirty = dirty.size > 0 || localDevDirty || !!themeTrial;

  // Unsaved Theme Builder work is saved by its own button (it writes the theme
  // table, not preferences), so it never enables header Save; it only makes
  // Cancel ask before discarding.
  const [themeBuilderDirty, setThemeBuilderDirty] = useState(false);

  // ── Permission-gated tab list ───────────────────────────────────────────
  const anyPermission = useCallback((test) => {
    if (isSysAdmin) return true;
    const perms = currentUser?.permissions || {};
    return Object.values(perms).some(list => (list || []).some(test));
  }, [isSysAdmin, currentUser]);

  const visibleTabs = useMemo(() => TAB_ORDER.filter(tab => {
    if (tab === 'Themes')        return !!isSysAdmin;
    if (tab === 'Security')      return anyPermission(k => k === 'preferences.security');
    if (tab === 'Debug')         return anyPermission(k => k === 'preferences.debug');
    if (tab === 'Local Dev')     return !!window.__config__?.IS_DEV_VM && anyPermission(k => k === 'preferences.debug');
    if (tab === 'Notifications') return anyPermission(k => k.startsWith('reports.'));
    return true;
  }), [anyPermission, isSysAdmin]);

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab(visibleTabs[0] || 'General');
  }, [visibleTabs, activeTab]);

  // ── Data loads ──────────────────────────────────────────────────────────
  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setAvailableDatabases(res.data || []))
      .catch(() => {});
  }, []);

  // databases/available answers for the signed-in account, so while a SysAdmin impersonates it
  // lists every database. Narrow it to the databases the person being edited holds a role in.
  const databases = useMemo(() => (isSysAdmin
    ? availableDatabases
    : availableDatabases.filter(db => currentUser?.roles?.[(db.slug || '').toLowerCase()])),
  [availableDatabases, isSysAdmin, currentUser]);

  useEffect(() => {
    const loadLabels = async () => {
      const [appDef, local, remote] = await Promise.all([
        getLocationLabel('app-default'),
        getLocationLabel('local-reports'),
        getLocationLabel('remote-reports'),
      ]);
      setLocationLabels({ 'app-default': appDef, 'local-reports': local, 'remote-reports': remote });
    };
    loadLabels();
  }, []);

  // ── Dirty staging ───────────────────────────────────────────────────────
  const targetOf = (def) => scopeTarget[def.prefKey] ?? 'global';

  const scopeRowFor = useCallback((def, target) => {
    const row = { prefKey: def.prefKey, databaseInstanceId: null, machineId: null };
    if (typeof target === 'number' && def.allowPerDatabase) row.databaseInstanceId = target;
    if (target === 'machine' && def.allowPerMachine) row.machineId = machineId;
    return row;
  }, [machineId]);

  /** Existing saved row at a given scope, if any. */
  const savedRowAt = useCallback((def, target) => {
    const s = scopeRowFor(def, target);
    return rows.find(r =>
      r.prefKey === def.prefKey &&
      (r.databaseInstanceId ?? null) === s.databaseInstanceId &&
      (r.machineId ?? null) === s.machineId) || null;
  }, [rows, scopeRowFor]);

  /** Value shown for the currently selected scope target: staged at that scope,
   *  else saved at that scope, else staged global, else the global resolution.
   *  A staged null (cleared override) falls through to the global value. */
  const stagedValue = useCallback((def) => {
    const target = targetOf(def);
    const atScope = dirty.get(scopeKeyOf(scopeRowFor(def, target)));
    if (atScope && atScope.value != null) return atScope.value;
    if (!atScope) {
      const saved = savedRowAt(def, target);
      if (saved) return saved.value;
    }
    const global = dirty.get(scopeKeyOf(scopeRowFor(def, 'global')));
    if (global && global.value != null) return global.value;
    // '' slug forces global-context resolution (skips any current-DB override)
    return target === 'global' ? getPref(def.prefKey) : getPref(def.prefKey, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, scopeTarget, scopeRowFor, savedRowAt, getPref]);

  const stage = useCallback((def, value) => {
    const row = { ...scopeRowFor(def, targetOf(def)), value: value == null ? null : String(value) };
    setDirty(prev => {
      const next = new Map(prev);
      next.set(scopeKeyOf(row), row);
      return next;
    });
    setSaveErrors(prev => { const n = { ...prev }; delete n[def.prefKey]; return n; });
    // Live preview
    if (value != null) {
      if (def.prefKey === 'display.theme') applyThemeToDom(value);
      if (def.prefKey === 'display.uiZoom') applyZoomToDom(value);
      if (def.prefKey === 'display.density') applyDensityToDom(value);
      if (def.prefKey === 'display.motion') applyMotionToDom(value);
      if (def.prefKey === 'display.timezone') setUserTimeZone(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeRowFor, scopeTarget]);

  const changeScopeTarget = (def, target) => {
    setScopeTarget(prev => ({ ...prev, [def.prefKey]: target }));
    if (def.prefKey === 'display.theme' && trial) setTrialScope(target);
  };

  // ── Theme pick (a trial aimed at the theme's scope) ─────────────────────
  const themeDef = catalog.find(d => d.prefKey === 'display.theme') || null;

  /** The theme in effect at the theme scope, ignoring an unkept pick. */
  const savedThemeAtScope = () => (themeDef ? stagedValue(themeDef) : getPref('display.theme'));

  const pickTheme = (key) => {
    if (!themeDef || !key) return;
    if (key === savedThemeAtScope()) { if (themeTrial) stopTrial(); return; }
    const t = themes.find(x => x.themeKey === key);
    if (!t) return;
    setSaveErrors(prev => { const n = { ...prev }; delete n['display.theme']; return n; });
    startTrial({ key: t.themeKey, label: t.label, tokens: t.tokens, scope: targetOf(themeDef), source: 'preferences' });
  };

  // ── Save / Cancel ───────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setSaveErrors({});
    try {
      const batch = [...dirty.values()];
      if (themeTrial) {
        const row = { ...scopeRowFor(themeDef, themeTrial.scope), value: themeTrial.key };
        const k = scopeKeyOf(row);
        batch.splice(0, batch.length, ...batch.filter(r => scopeKeyOf(r) !== k), row);
      }
      if (batch.length > 0) await savePrefs(batch);
      if (localDevSaveRef.current) await localDevSaveRef.current();
      setDirty(new Map());
      setIsEditing(false);
    } catch (e) {
      const errs = e?.response?.data?.errors || [];
      const map = {};
      errs.forEach(er => { if (er.prefKey) map[er.prefKey] = er.error; });
      setSaveErrors(Object.keys(map).length ? map : { _global: 'Save failed — check connection and try again.' });
    } finally {
      setSaving(false);
    }
  };

  const discardEdits = useCallback(() => {
    setDirty(new Map());
    setSaveErrors({});
    setIsEditing(false);
    if (themeTrial) stopTrial();   // stopTrial re-applies the saved visuals itself
    else reapplyVisuals();         // revert live previews
  }, [reapplyVisuals, themeTrial, stopTrial]);

  const handleCancel = () => {
    if (!isDirty && !themeBuilderDirty) { reapplyVisuals(); setIsEditing(false); return; }
    // An unkept theme pick alone needs no confirm: the theme already shows what Cancel undoes.
    if (dirty.size === 0 && !localDevDirty && !themeBuilderDirty) { discardEdits(); return; }
    notify.confirm('Discard unsaved preference changes?', discardEdits, {
      confirmText: 'Discard', cancelText: 'Keep Editing',
    });
  };

  // The theme picker opens on the scope that paints this screen: the current database when it
  // has its own theme row, otherwise All Databases. Other prefs keep their last scope.
  const startEditing = () => {
    if (themeDef && !trial && scopeTarget['display.theme'] == null) {
      const dbId = slugToDbId[activeDbSlug()];
      if (dbId != null && rows.some(r => r.prefKey === 'display.theme' && r.databaseInstanceId === dbId)) {
        setScopeTarget(prev => ({ ...prev, 'display.theme': dbId }));
      }
    }
    setIsEditing(true);
  };

  const handleRestoreDefaults = async () => {
    await restoreDefaults();
    setDirty(new Map());
    setRestoreConfirm(false);
    setIsEditing(false);
  };

  // ── Control renderers ───────────────────────────────────────────────────
  // inline: sits beside the control on one line instead of under the label.
  const renderScopePicker = (def, inline = false) => {
    const target = targetOf(def);

    // DB-scoped: combo box — All Databases + every DB the user can access.
    // Any database's override is viewable/editable from anywhere.
    if (def.allowPerDatabase) {
      const hasOverride = typeof target === 'number' &&
        (savedRowAt(def, target) || (dirty.get(scopeKeyOf(scopeRowFor(def, target)))?.value != null));
      return (
        <div className={`stui-row-setting-controls${inline ? '' : ' stui-row-setting-scope'}`}>
          <span className="stui-sublabel">Apply to:</span>
          <StuiSelect
            className="stui-select"
            value={String(target)}
            disabled={!isEditing}
            onChange={e => changeScopeTarget(def, e.target.value === 'global' ? 'global' : Number(e.target.value))}
          >
            <option value="global">All Databases</option>
            {databases.map(db => {
              const name = db.displayName || db.name;
              const dbId = slugToDbId[(db.slug || name || '').toLowerCase()];
              return dbId != null ? <option key={dbId} value={String(dbId)}>{name}</option> : null;
            })}
          </StuiSelect>
          {hasOverride && isEditing && (
            <button className="stui-btn stui-btn-secondary" title="Remove this database's override — falls back to All Databases"
              onClick={() => { if (def.prefKey === 'display.theme' && themeTrial?.scope === target) stopTrial(); stage(def, null); }}>
              <X size={12} /> Clear override
            </button>
          )}
        </div>
      );
    }

    // Machine-scoped: only ever two options — pills stay.
    return (
      <div className="stui-pill-group stui-row-setting-scope">
        <span className="stui-sublabel">Apply to:</span>
        <button className={`stui-pill${target === 'global' ? ' selected' : ''}`} disabled={!isEditing}
          onClick={() => changeScopeTarget(def, 'global')}>All machines</button>
        <button className={`stui-pill${target === 'machine' ? ' selected' : ''}`} disabled={!isEditing}
          onClick={() => changeScopeTarget(def, 'machine')}>This machine</button>
      </div>
    );
  };

  const renderControl = (def) => {
    const isFuture = def.status === 'future';
    const disabled = isFuture || !isEditing || failSoft;
    const value = stagedValue(def);
    let options = parseOptions(def);
    if (options) options = options.filter(o => !o.sysAdminOnly || currentUser?.isSysAdmin);
    const err = saveErrors[def.prefKey];

    // Special: default database — options come from the access-filtered DB list
    if (def.prefKey === 'general.defaultDatabase') {
      return (
        <div key={def.prefKey} className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label">{def.label}</div>
            <div className="stui-sublabel">{def.sublabel}</div>
            {err && <div className="stui-sublabel stui-text-error">{err}</div>}
          </div>
          <StuiSelect value={value || ''} onChange={e => stage(def, e.target.value)} className="stui-select" disabled={disabled}>
            <option value="">— No default (show picker) —</option>
            {databases.map(db => {
              const name = db.displayName || db.name;
              const slug = (db.slug || name)?.toLowerCase();
              return <option key={db.id} value={slug}>{name}</option>;
            })}
          </StuiSelect>
        </div>
      );
    }

    if (def.dataType === 'bool') {
      return (
        <ToggleRow
          key={def.prefKey}
          label={def.label}
          sub={err ? `${def.sublabel || ''} — ${err}` : def.sublabel}
          checked={value === 'true'}
          onChange={e => stage(def, e.target.checked ? 'true' : 'false')}
          disabled={disabled}
          badge={isFuture}
        />
      );
    }

    const control = options && options.length > 6 ? (
      <StuiSelect value={value || ''} onChange={e => stage(def, e.target.value)} className="stui-select" disabled={disabled}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </StuiSelect>
    ) : options ? (
      <div className="stui-pill-group">
        {options.map(o => (
          <button key={o.value} className={`stui-pill${value === o.value ? ' selected' : ''}`}
            disabled={disabled} onClick={() => stage(def, o.value)}>{o.label}</button>
        ))}
      </div>
    ) : null;

    return (
      <div key={def.prefKey} className="stui-row-setting">
        <div className="stui-row-setting-label">
          <div className="stui-label">{def.label}{isFuture && <span className="stui-badge-construction">Under Construction</span>}</div>
          {def.sublabel && <div className="stui-sublabel">{def.sublabel}</div>}
          {(def.allowPerDatabase || def.allowPerMachine) && !isFuture && renderScopePicker(def)}
          {err && <div className="stui-sublabel stui-text-error">{err}</div>}
        </div>
        {control}
      </div>
    );
  };

  const renderTabContent = (tab) => {
    if (tab === 'Themes') {
      return (
        <ThemesTab
          isEditing={isEditing}
          onBuilderDirtyChange={setThemeBuilderDirty}
          themeDef={themeDef}
          selectedKey={themeTrial?.key ?? savedThemeAtScope()}
          onStageTheme={pickTheme}
          themeScopeTarget={themeDef ? targetOf(themeDef) : 'global'}
          renderScope={() => themeDef && renderScopePicker(themeDef)}
        />
      );
    }

    // Local Dev is file-backed, not catalog-backed — its own component owns the tab.
    if (tab === 'Local Dev') {
      return <LocalDevTab isEditing={isEditing} onDirtyChange={setLocalDevDirty} saveRef={localDevSaveRef} />;
    }

    const defs = catalog
      .filter(d => d.tab === tab && d.status !== 'hidden')
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Group by section (null section = unnamed leading group)
    const sections = [];
    defs.forEach(d => {
      const name = d.section || null;
      let group = sections.find(s => s.name === name);
      if (!group) { group = { name, defs: [] }; sections.push(group); }
      group.defs.push(d);
    });

    return (
      <>
        {tab === 'General' && (
          <>
            <SectionHeader title="Session Information" />
            <div className="stui-stat-row">
              <div className="stui-stat-item"><span className="stui-stat-label">Browser</span><span className="stui-stat-value">{session.browser}</span></div>
              <div className="stui-stat-item"><span className="stui-stat-label">Operating System</span><span className="stui-stat-value">{session.os}</span></div>
              <div className="stui-stat-item"><span className="stui-stat-label">Device</span><span className="stui-stat-value">{session.device}</span></div>
              <div className="stui-stat-item"><span className="stui-stat-label">User</span><span className="stui-stat-value">{currentUser?.user_Name || currentUser?.displayName || currentUser?.email || '—'}</span></div>
            </div>
          </>
        )}

        {tab === 'Display' && themeDef && (
          <>
            <SectionHeader title="Theme" />
            <ThemePickerRow
              isEditing={isEditing && !failSoft}
              value={themeTrial?.key ?? (isEditing ? savedThemeAtScope() : getPref('display.theme'))}
              onPick={pickTheme}
              scopeControl={renderScopePicker(themeDef, true)}
              scopeIsGlobal={targetOf(themeDef) === 'global'}
              error={saveErrors['display.theme']}
            />
          </>
        )}

        {sections.map((group, i) => (
          <React.Fragment key={group.name || `sec-${i}`}>
            {group.name && <SectionHeader title={group.name} />}
            {group.defs.map(renderControl)}
          </React.Fragment>
        ))}

        {tab === 'Notifications' && (
          <>
            <SectionHeader title="Following" />
            <ToggleRow
              label="Follow Records"
              sub="Watch specific records and get notified when they change. Planned: a database selector with a table of the records you follow."
              checked={false}
              onChange={() => {}}
              disabled
              badge
            />
          </>
        )}

        {tab === 'Downloads' && renderDownloadLocations()}

        {tab === 'General' && renderRestoreDefaults()}
      </>
    );
  };

  const handleBrowse = async (key) => {
    const label = await setDownloadLocation(key);
    if (label) setLocationLabels(prev => ({ ...prev, [key]: label }));
  };
  const handleClearLocation = async (key) => {
    await clearDownloadLocation(key);
    setLocationLabels(prev => ({ ...prev, [key]: null }));
  };

  const renderDownloadLocations = () => (
    <>
      <SectionHeader title="File Paths" />
      <div className="stui-label-note">
        Download folders are browser-bound and stay on this machine — they cannot follow you to other devices.
        The browser only exposes the selected folder's name, not its full path.
        {!fsSupported && ' Custom locations require Chrome or Edge; other browsers always show the system save dialog.'}
      </div>
      {LOCATION_KEYS.map(({ key, label, sub }) => (
        <div key={key} className="stui-row-setting">
          <div className="stui-row-setting-label">
            <div className="stui-label">{label}</div>
            <div className="stui-sublabel">{sub}</div>
          </div>
          <div className="stui-row-setting-controls">
            {locationLabels[key] ? (
              <>
                <span>{locationLabels[key]}</span>
                <button className="stui-btn stui-btn-primary" onClick={() => handleBrowse(key)} disabled={!fsSupported}><FolderOpen size={13} /> Change</button>
                <button className="stui-btn stui-btn-secondary" onClick={() => handleClearLocation(key)} title="Clear stored location"><X size={12} /></button>
              </>
            ) : (
              <>
                <span>{key === 'app-default' ? 'System default (browser downloads)' : 'Use Application Default'}</span>
                <button className="stui-btn stui-btn-primary" onClick={() => handleBrowse(key)} disabled={!fsSupported}><FolderOpen size={13} /> Browse...</button>
              </>
            )}
          </div>
        </div>
      ))}
    </>
  );

  const renderRestoreDefaults = () => (
    <div className="stui-row-setting">
      <div className="stui-row-setting-label">
        <div className="stui-label">Restore Defaults</div>
        <div className="stui-sublabel">
          {isImpersonating
            ? `Reset ALL of ${currentUser?.user_Name || currentUser?.email}'s preferences to defaults`
            : 'Reset all your preferences to their default values (all databases, all machines)'}
        </div>
      </div>
      <div className="stui-row-setting-controls">
        {!restoreConfirm ? (
          <button className="stui-btn stui-btn-primary" onClick={() => setRestoreConfirm(true)} disabled={failSoft}>
            <RefreshCw size={13} /> Restore Defaults
          </button>
        ) : (
          <>
            <AlertTriangle size={14} />
            <span className="stui-sublabel">This deletes all saved preferences. Continue?</span>
            <button className="stui-btn stui-btn-primary" onClick={handleRestoreDefaults}>Yes, reset</button>
            <button className="stui-btn stui-btn-secondary" onClick={() => setRestoreConfirm(false)}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────
  // STUI page shell per the style guide (STUIPage.js): white module-container
  // frame → frozen module header → frozen ssg tab bar → scrollable tab body.
  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Settings size={18} />
          <h1>Preferences</h1>
          {isImpersonating && (
            <span className="stui-badge-construction">
              <UserCheck size={12} /> Editing for {currentUser?.user_Name || currentUser?.email} — Save writes THEIR preferences
            </span>
          )}
        </div>
        <div className="stui-module-header-actions">
          <button
            className="stui-crud-btn"
            title={isEditing ? 'Cancel editing' : 'Edit preferences'}
            onClick={isEditing ? handleCancel : startEditing}
            disabled={failSoft || !hydrated}
          >
            {isEditing ? <X size={16} /> : <Pencil size={16} />}
          </button>
          <button
            className={`stui-crud-btn ${!(isEditing && isDirty) || saving ? 'disabled' : ''}`}
            title="Save preferences"
            onClick={handleSave}
            disabled={!(isEditing && isDirty) || saving}
          >
            <Save size={16} />
          </button>
        </div>
      </div>

      <div className="stui-ssg-tab-bar">
        {visibleTabs.map(tab => (
          <button
            key={tab}
            className={`stui-ssg-tab-btn${activeTab === tab ? ' active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="stui-tab-body">
        {failSoft && (
          <div className="stui-label-note stui-text-error">
            Preference service unavailable — showing cached/default values. Changes cannot be saved right now.
          </div>
        )}
        {saveErrors._global && (
          <div className="stui-label-note stui-text-error">{saveErrors._global}</div>
        )}
        {renderTabContent(activeTab)}
      </div>
    </div>
  );
};

export default PreferencesPage;
